import { useMemo } from 'react';
import Raven from 'raven-js';
import { registerQuery, useQuery } from 'data-fetching-client';
import getLang from 'I18n/utils/getLang';

import { AppConnectionStatuses } from 'integrations-lib/constants/appConnectionStatuses';

import { chirpClient } from '../http/chirpClient';

import {
  fetchAllForUserRpc,
  StatusTypeCategory,
} from '../../__generated__/chirp/com/hubspot/connected/application/status/rpc/frontend/ConnectedStatusGraphQLService';

interface GetAppStatusRequest {
  appId: number;
  categories?: StatusTypeCategory[];
}

export const GET_APP_STATUS = registerQuery({
  fieldName: 'connectedAppStatus',
  args: ['appId', 'categories'],
  fetcher: (request: GetAppStatusRequest) => {
    return chirpClient.call(fetchAllForUserRpc, {
      request: {
        locale: getLang(),
        statusFetchOptions: {
          appIds: [request.appId],
          categories: request.categories,
        },
      },
    });
  },
});

export const useGetAppStatuses = ({
  appId,
  categories,
}: {
  appId?: number;
  categories?: StatusTypeCategory[];
}) => {
  const { data, loading, error, refetch } = useQuery(GET_APP_STATUS, {
    variables: {
      appId: appId!,
      categories,
    },
    skip: !appId,
    onError: (err) => {
      Raven.captureException(err, {
        extra: {
          appId,
        },
      });
    },
  });

  const filteredStatuses = useMemo(
    () =>
      (data?.connectedAppStatus?.results || []).filter(
        (status) => status.type !== AppConnectionStatuses.NO_STATUS_AVAILABLE
      ),
    [data]
  );

  return {
    data: filteredStatuses,
    loading,
    error,
    refetch,
  };
};
